import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  X,
  Sparkles,
  User,
  Briefcase,
  GraduationCap,
  Heart,
  MapPin,
  Target,
  Youtube,
  FileText,
  Lightbulb,
  Copy,
  Check,
  Loader2,
  Zap,
  ArrowDown,
  Wand2,
  ImagePlus,
  ClipboardPaste,
  ThumbsUp,
  MessageCircle,
  Repeat2,
  Send,
  MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

// Post templates — add new entries here to extend the system
const TEMPLATES = {
  'story': {
    name: 'Story',
    subtitle: 'Lesson learned',
    emoji: '📖',
    description: 'Journey with a turning point',
    nodes: [
      { id: 'belief', label: 'What I Believed', prompt: 'What was your initial assumption or belief?', row: 0, col: 0 },
      { id: 'happened', label: 'What Happened', prompt: 'What event challenged that belief?', row: 1, col: 0 },
      { id: 'turning', label: 'The Turning Point', prompt: 'What was the moment of realization?', row: 2, col: 0 },
      { id: 'changed', label: 'What Changed', prompt: 'How did your thinking shift?', row: 3, col: 0 },
      { id: 'insight', label: 'The Broader Insight', prompt: 'What universal truth did you discover?', row: 4, col: 0 },
      { id: 'application', label: 'For the Reader', prompt: 'How can others apply this?', row: 5, col: 0 },
    ],
    promptBlueprint: 'Story post: open with tension (belief vs reality), build to turning point, land on universal insight. Emphasize emotional arc. End with reader takeaway.',
  },
  'list': {
    name: 'List',
    subtitle: 'Actionable tips',
    emoji: '📋',
    description: 'Scannable numbered insights',
    nodes: [
      { id: 'topic', label: 'The Topic', prompt: 'What topic are you sharing tips about?', row: 0, col: 0 },
      { id: 'count', label: 'Number of Items', prompt: 'How many items? (e.g. 5, 7, 10)', row: 1, col: 0 },
      { id: 'items', label: 'The Items', prompt: 'List each tip, one per line (e.g. "1. Do X because Y")', row: 2, col: 0 },
      { id: 'closing', label: 'Closing Insight', prompt: 'What meta-lesson ties them all together?', row: 3, col: 0 },
    ],
    promptBlueprint: 'List post: bold numbered format, each item short and punchy, ordered by impact or logic, closing line synthesizes a meta-insight. Hook = intriguing count or bold claim.',
  },
  'hot-take': {
    name: 'Hot Take',
    subtitle: 'Contrarian opinion',
    emoji: '🔥',
    description: 'Challenge conventional wisdom',
    nodes: [
      { id: 'common', label: 'Common Belief', prompt: 'What does everyone commonly believe or do?', row: 0, col: 0 },
      { id: 'hottake', label: 'Your Hot Take', prompt: 'What is your contrarian position?', row: 1, col: 0 },
      { id: 'evidence', label: 'Your Evidence', prompt: 'What proves your point? (data, story, observation)', row: 2, col: 0 },
      { id: 'defense', label: 'Defend It', prompt: 'Anticipate the pushback — how do you address it?', row: 3, col: 0 },
      { id: 'takeaway', label: 'The Takeaway', prompt: 'What should people do differently after reading this?', row: 4, col: 0 },
    ],
    promptBlueprint: 'Hot take post: open with bold polarizing opinion, defend with evidence, acknowledge pushback, land on clear takeaway. Confident but not arrogant. Hook = the opinion itself.',
  },
  'question': {
    name: 'Question',
    subtitle: 'Engagement-first',
    emoji: '❓',
    description: 'Lead with curiosity',
    nodes: [
      { id: 'observation', label: 'The Observation', prompt: 'What have you noticed that others might relate to?', row: 0, col: 0 },
      { id: 'question', label: 'The Core Question', prompt: 'What big question does your observation raise?', row: 1, col: 0 },
      { id: 'myview', label: 'Your Take', prompt: 'What is your perspective or answer?', row: 2, col: 0 },
      { id: 'readerq', label: 'Reader Application', prompt: 'How does this apply to readers? What do you want them to reflect on?', row: 3, col: 0 },
    ],
    promptBlueprint: 'Question post: relatable observation, thought-provoking question, personal perspective (non-preachy), open invitation for readers to share. Hook = the question or the observation leading into it.',
  },
  'announcement': {
    name: 'Announcement',
    subtitle: 'Updates & news',
    emoji: '📣',
    description: 'Share news with context',
    nodes: [
      { id: 'context', label: 'The Context', prompt: 'What is the backstory or context for this announcement?', row: 0, col: 0 },
      { id: 'announcement', label: 'The News', prompt: 'What is the actual announcement?', row: 1, col: 0 },
      { id: 'why', label: 'Why It Matters', prompt: 'Why should your audience care about this?', row: 2, col: 0 },
      { id: 'next', label: "What's Next", prompt: 'What happens next? What are you excited about?', row: 3, col: 0 },
    ],
    promptBlueprint: 'Announcement post: lead with context before the reveal for narrative tension, explain significance from audience perspective, close with forward-looking excitement. Authentic voice, not press-release stiff.',
  },
  'celebration': {
    name: 'Celebration',
    subtitle: 'Wins & milestones',
    emoji: '🎉',
    description: 'Share achievements humbly',
    nodes: [
      { id: 'achievement', label: 'The Achievement', prompt: 'What is the win or milestone you are celebrating?', row: 0, col: 0 },
      { id: 'journey', label: 'The Journey', prompt: 'What challenges did you face along the way?', row: 1, col: 0 },
      { id: 'gratitude', label: 'Who Helped', prompt: 'Who supported you or played a key role?', row: 2, col: 0 },
      { id: 'learned', label: 'What I Learned', prompt: 'What did this journey teach you?', row: 3, col: 0 },
      { id: 'forward', label: "What's Next", prompt: 'Where do you go from here?', row: 4, col: 0 },
    ],
    promptBlueprint: 'Celebration post: humble opening (journey not just the win), genuine specific gratitude, shareable insight others can learn from, forward-looking energy. Hook = the journey or challenge, not the trophy.',
  },
  'case-study': {
    name: 'Case Study',
    subtitle: 'Problem → solution → result',
    emoji: '📊',
    description: 'Walk through a real problem solved',
    nodes: [
      { id: 'context', label: 'The Context', prompt: 'Set the scene — what was the situation or project?', row: 0, col: 0 },
      { id: 'problem', label: 'The Problem', prompt: 'What was the specific challenge or pain point?', row: 1, col: 0 },
      { id: 'action', label: 'What I Did', prompt: 'What steps did you take to address the problem?', row: 2, col: 0 },
      { id: 'result', label: 'The Result', prompt: 'What was the outcome? (quantify if possible)', row: 3, col: 0 },
      { id: 'insight', label: 'The Insight', prompt: 'What was the surprising or generalizable lesson?', row: 4, col: 0 },
    ],
    promptBlueprint: 'Case study post: concrete context, specific problem readers recognize, clear action steps, measurable results, insight that generalizes beyond this case. Hook = the problem or surprising result.',
  },
};

const TONES = [
  { id: 'storyteller', label: 'Storyteller' },
  { id: 'thought-leader', label: 'Thought Leader' },
  { id: 'humorous', label: 'Humorous' },
  { id: 'bold', label: 'Bold & Punchy' },
  { id: 'inspiring', label: 'Inspiring' },
  { id: 'conversational', label: 'Conversational' },
  { id: 'professional', label: 'Professional' },
];

const LENGTHS = [
  { id: 'short', label: 'Short', desc: '~100 words' },
  { id: 'medium', label: 'Medium', desc: '~200 words' },
  { id: 'long', label: 'Long', desc: '~300 words' },
];

// Stick figure SVG — idle and running (kicking legs) variants
const StickFigureIdle = () => (
  <svg viewBox="0 0 40 56" width="28" height="38" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* head */}
    <circle cx="20" cy="7" r="5.5" stroke="white" strokeWidth="2.2" />
    {/* body */}
    <line x1="20" y1="13" x2="20" y2="32" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* left arm */}
    <line x1="20" y1="17" x2="9" y2="25" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* right arm */}
    <line x1="20" y1="17" x2="31" y2="25" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* left leg */}
    <line x1="20" y1="32" x2="12" y2="46" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* right leg */}
    <line x1="20" y1="32" x2="28" y2="46" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const StickFigureRunning = () => (
  <svg viewBox="0 0 40 56" width="28" height="38" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ animation: 'pegman-run 0.35s steps(1) infinite' }}>
    <style>{`
      @keyframes pegman-leg-l {
        0%, 100% { transform-origin: 20px 32px; transform: rotate(-35deg); }
        50%       { transform-origin: 20px 32px; transform: rotate(35deg); }
      }
      @keyframes pegman-leg-r {
        0%, 100% { transform-origin: 20px 32px; transform: rotate(35deg); }
        50%       { transform-origin: 20px 32px; transform: rotate(-35deg); }
      }
      @keyframes pegman-arm-l {
        0%, 100% { transform-origin: 20px 17px; transform: rotate(15deg); }
        50%       { transform-origin: 20px 17px; transform: rotate(-15deg); }
      }
      @keyframes pegman-arm-r {
        0%, 100% { transform-origin: 20px 17px; transform: rotate(-15deg); }
        50%       { transform-origin: 20px 17px; transform: rotate(15deg); }
      }
      .pg-ll { animation: pegman-leg-l 0.4s ease-in-out infinite; }
      .pg-lr { animation: pegman-leg-r 0.4s ease-in-out infinite; }
      .pg-al { animation: pegman-arm-l 0.4s ease-in-out infinite; }
      .pg-ar { animation: pegman-arm-r 0.4s ease-in-out infinite; }
    `}</style>
    {/* head */}
    <circle cx="20" cy="7" r="5.5" stroke="white" strokeWidth="2.2" />
    {/* body */}
    <line x1="20" y1="13" x2="20" y2="32" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* left arm */}
    <line className="pg-al" x1="20" y1="17" x2="9" y2="25" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* right arm */}
    <line className="pg-ar" x1="20" y1="17" x2="31" y2="25" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* left leg */}
    <line className="pg-ll" x1="20" y1="32" x2="12" y2="46" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    {/* right leg */}
    <line className="pg-lr" x1="20" y1="32" x2="28" y2="46" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

// 1x1 transparent GIF — used to suppress the browser's built-in drag ghost
const EMPTY_DRAG_IMG = (() => {
  const img = new Image();
  img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
  return img;
})();

const Pegman = ({ onDragStart }) => {
  const [dragging, setDragging] = React.useState(false);
  const [pos, setPos] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!dragging) return;
    const onDragOver = (e) => setPos({ x: e.clientX, y: e.clientY });
    document.addEventListener('dragover', onDragOver);
    return () => document.removeEventListener('dragover', onDragOver);
  }, [dragging]);

  return (
    <>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setDragImage(EMPTY_DRAG_IMG, 0, 0); // hide browser ghost
          e.dataTransfer.setData('action', 'pegman');
          e.dataTransfer.effectAllowed = 'copy';
          setPos({ x: e.clientX, y: e.clientY });
          setDragging(true);
          onDragStart?.(e);
        }}
        onDragEnd={() => setDragging(false)}
        className="w-14 h-14 rounded-2xl flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg hover:shadow-xl transition-all hover:scale-105 border-4 border-white"
        style={{ backgroundColor: '#000000', opacity: dragging ? 0 : 1 }}
        title="Drag to canvas to add a node or drop on a node for AI help"
      >
        <StickFigureIdle />
      </div>

      {dragging && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            left: pos.x - 28,
            top: pos.y - 36,
            width: 56,
            height: 56,
            backgroundColor: '#000000',
            borderRadius: 14,
            border: '3px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'rotate(-10deg) scale(1.2)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          <StickFigureRunning />
        </div>,
        document.body
      )}
    </>
  );
};

// Inspiration Paste Box - paste LinkedIn post text directly
const InspirationPasteBox = ({ value, onChange, onAnalyze, isAnalyzing }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <div
        className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden transition-all"
        style={{ boxShadow: '4px 4px 0px rgba(0,0,0,0.05)' }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#a8d0e8'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
      >
        <button
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-slate-50/50 hover:bg-slate-100/60 transition-colors"
        >
          <div className="flex items-center gap-2">
            <ClipboardPaste className="w-4 h-4" style={{ color: '#63a5d3' }} />
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Paste for inspiration</span>
            {value.trim() && <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />}
          </div>
          <ArrowDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Copy & paste a LinkedIn post you admire here..."
            className="w-full h-32 p-4 text-sm font-medium text-slate-700 leading-relaxed resize-none focus:outline-none border-t border-slate-100 placeholder:text-slate-400"
            style={{ fontFamily: "'Inter', sans-serif" }}
          />
        )}
      </div>
      {open && value.trim() && (
        <button
          onClick={onAnalyze}
          disabled={isAnalyzing}
          className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all"
          style={{
            boxShadow: '3px 3px 0px rgba(0,0,0,0.08)',
            backgroundColor: isAnalyzing ? '#e3f0f7' : '#63a5d3',
            color: isAnalyzing ? '#3d7ba0' : '#ffffff',
          }}
          onMouseEnter={(e) => !isAnalyzing && (e.currentTarget.style.backgroundColor = '#4a8bb8')}
          onMouseLeave={(e) => !isAnalyzing && (e.currentTarget.style.backgroundColor = '#63a5d3')}
        >
          {isAnalyzing ? (
            <><Loader2 className="w-4 h-4 animate-spin" />Analyzing structure...</>
          ) : (
            <><Sparkles className="w-4 h-4" />Generate Framework</>
          )}
        </button>
      )}
    </div>
  );
};

// Media Upload Box - Redesigned with 3 image limit
const MediaUploadBox = ({ images, onUpload, onRemove }) => {
  const fileInputRef = useRef(null);
  const maxImages = 3;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = maxImages - images.length;

    files.slice(0, remainingSlots).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          onUpload(event.target.result);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  return (
    <div className="grid grid-cols-3 gap-3">
      {[0, 1, 2].map((idx) => {
        const img = images[idx];
        return (
          <div
            key={idx}
            onClick={() => !img && fileInputRef.current?.click()}
            className={`relative aspect-square rounded-2xl overflow-hidden transition-all cursor-pointer ${
              img
                ? 'border-2 border-slate-200'
                : 'border-2 border-dashed border-slate-300'
            }`}
            onMouseEnter={(e) => {
              if (images.length < 3) {
                e.currentTarget.style.borderColor = '#63a5d3';
                e.currentTarget.style.backgroundColor = 'rgba(227, 240, 247, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (images.length < 3) {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            style={{ boxShadow: img ? '4px 4px 0px rgba(0,0,0,0.05)' : 'none' }}
          >
            {img ? (
              <>
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                <ImagePlus className="w-5 h-5 text-slate-400" />
              </div>
            )}
          </div>
        );
      })}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

// Flow Node Component
const FlowNode = ({ node, onUpdate, onDelete, onAiHelp, isAiLoading }) => {
  const [content, setContent] = useState(node.content || '');
  const [isDragOver, setIsDragOver] = useState(false);
  const prevNodeContentRef = useRef(node.content);
  const isFilled = content.trim().length > 0;
  const isPrefilled = node.prefilled;

  // Sync local state when parent replaces node content (e.g. AI pre-fill or framework switch)
  useEffect(() => {
    if (node.content !== undefined && node.content !== prevNodeContentRef.current) {
      setContent(node.content);
      prevNodeContentRef.current = node.content;
    }
  }, [node.content]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const action = e.dataTransfer.getData('action');
    if (action === 'pegman') {
      onAiHelp(node.id);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      className={`relative w-64 rounded-3xl transition-all ${
        isDragOver
          ? 'ring-4 ring-offset-2'
          : isFilled
            ? isPrefilled
              ? 'bg-blue-50 border-3 border-blue-300'
              : 'bg-white border-3 border-slate-200'
            : 'border-3 border-dashed'
      }`}
      style={{
        boxShadow: isDragOver ? '0 8px 24px rgba(99, 165, 211, 0.3)' : '6px 6px 0px rgba(0,0,0,0.06)',
        borderWidth: '3px',
        backgroundColor: isDragOver ? '#e3f0f7' : (isFilled ? (isPrefilled ? '#dbeafe' : '#ffffff') : '#f0f7fb'),
        borderColor: isDragOver ? '#63a5d3' : (isFilled ? (isPrefilled ? '#93c5fd' : '#e2e8f0') : '#a8d0e8'),
        ringColor: isDragOver ? '#63a5d3' : undefined,
      }}
    >
      {isAiLoading && (
        <div className="absolute inset-0 bg-white/90 rounded-3xl flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#63a5d3' }} />
            <span className="text-xs font-bold" style={{ color: '#3d7ba0' }}>Writing...</span>
          </div>
        </div>
      )}

      <div className={`px-4 py-3 border-b-3 flex items-center justify-between ${
        isFilled ? (isPrefilled ? 'border-blue-200 bg-blue-100/50' : 'border-slate-100') : ''
      } rounded-t-3xl`}
      style={{
        borderBottomWidth: '3px',
        borderColor: isFilled ? (isPrefilled ? '#bfdbfe' : '#f1f5f9') : '#c8e1f0',
        backgroundColor: isFilled ? (isPrefilled ? 'rgba(219, 234, 254, 0.5)' : 'transparent') : 'rgba(227, 240, 247, 0.5)',
      }}
      >
        <span className={`text-xs font-black uppercase tracking-wider ${
          isFilled ? (isPrefilled ? 'text-blue-700' : 'text-slate-600') : ''
        }`}
        style={{
          fontFamily: "'Montserrat', sans-serif",
          color: isFilled ? (isPrefilled ? '#1d4ed8' : '#475569') : '#2d5a7a',
        }}
        >
          {node.label}
        </span>
        <button
          onClick={() => onDelete(node.id)}
          className="p-1.5 hover:bg-white/50 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-slate-400 hover:text-red-500" />
        </button>
      </div>

      <div className="p-4">
        <textarea
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            onUpdate(node.id, e.target.value);
          }}
          placeholder={node.prompt}
          className={`w-full bg-transparent text-sm font-medium leading-relaxed resize-none min-h-[80px] focus:outline-none ${
            isFilled ? 'text-slate-700 placeholder:text-slate-400' : 'placeholder:text-blue-300'
          }`}
          style={{
            color: isFilled ? '#334155' : '#2d5a7a',
          }}
        />
      </div>

      <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white ${
        isFilled ? 'bg-emerald-500' : ''
      }`}
      style={{
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: isFilled ? '#10b981' : '#63a5d3',
      }}
      />
    </motion.div>
  );
};

// Arrow connector between nodes
const NodeConnector = ({ direction = 'down' }) => {
  return (
    <div className={`flex ${direction === 'right' ? 'flex-row items-center mx-3' : 'flex-col items-center my-3'}`}>
      <div className={`${direction === 'right' ? 'w-10 h-1' : 'w-1 h-10'} bg-slate-300 rounded-full`} />
      <ArrowDown className={`w-5 h-5 text-slate-400 ${direction === 'right' ? '-rotate-90 -ml-1.5' : '-mt-1.5'}`} />
    </div>
  );
};

// Control Panel — post length, tone, and style toggles
const ControlPanel = ({
  postLength, setPostLength,
  postTone, setPostTone,
  includeEmojis, setIncludeEmojis,
  includeHashtags, setIncludeHashtags,
  includeCTA, setIncludeCTA,
  includeHook, setIncludeHook,
}) => (
  <div className="space-y-5">
    {/* Length */}
    <div>
      <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        Post Length
      </p>
      <div className="flex gap-2">
        {LENGTHS.map(l => (
          <button
            key={l.id}
            onClick={() => setPostLength(l.id)}
            className="flex-1 py-2 px-1 rounded-xl text-center transition-all border-2"
            style={{
              borderColor: postLength === l.id ? '#63a5d3' : '#e2e8f0',
              backgroundColor: postLength === l.id ? '#e3f0f7' : '#ffffff',
              color: postLength === l.id ? '#1d4ed8' : '#64748b',
            }}
          >
            <div className="text-xs font-bold">{l.label}</div>
            <div className="text-xs opacity-70">{l.desc}</div>
          </button>
        ))}
      </div>
    </div>

    {/* Tone */}
    <div>
      <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        Tone
      </p>
      <div className="flex flex-wrap gap-1.5">
        {TONES.map(t => (
          <button
            key={t.id}
            onClick={() => setPostTone(t.id)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all border-2"
            style={{
              borderColor: postTone === t.id ? '#63a5d3' : '#e2e8f0',
              backgroundColor: postTone === t.id ? '#e3f0f7' : '#ffffff',
              color: postTone === t.id ? '#1d4ed8' : '#64748b',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>

    {/* Toggles */}
    <div>
      <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        Style Options
      </p>
      <div className="space-y-2.5">
        {[
          { label: 'Strong Hook', value: includeHook, setter: setIncludeHook },
          { label: 'Call to Action', value: includeCTA, setter: setIncludeCTA },
          { label: 'Hashtags', value: includeHashtags, setter: setIncludeHashtags },
          { label: 'Emojis', value: includeEmojis, setter: setIncludeEmojis },
        ].map(({ label, value, setter }) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600">{label}</span>
            <button
              onClick={() => setter(!value)}
              className="relative w-10 h-5 rounded-full transition-colors flex-shrink-0"
              style={{ backgroundColor: value ? '#63a5d3' : '#cbd5e1' }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                style={{ transform: value ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// LinkedIn-style preview panel
const LinkedInPreview = ({ versions, images, onCopy, isVisible, userName, userRole, paywallActive, selectedVersion, onSelectVersion }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!isVisible || !versions || versions.length === 0) return null;

  const current = versions[selectedVersion] || versions[0] || {};
  const fullContent = [current.hook, current.body].filter(Boolean).join('\n\n');
  const PREVIEW_LIMIT = 280;
  const isLong = fullContent.length > PREVIEW_LIMIT;

  const handleCopy = async () => {
    try {
      if (paywallActive) { onCopy?.(); return; }
      await navigator.clipboard.writeText(fullContent);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const renderText = (text) => {
    const display = !expanded && isLong ? text.slice(0, PREVIEW_LIMIT) : text;
    return display.split('\n').map((line, i, arr) => (
      <React.Fragment key={i}>
        {line.split(/(#\w+)/g).map((part, j) =>
          part.startsWith('#')
            ? <span key={j} style={{ color: '#0a66c2' }}>{part}</span>
            : part
        )}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col w-full">
      {/* Header + Copy */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
        <h3 className="text-lg font-black text-slate-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Preview
        </h3>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
          style={{ boxShadow: '3px 3px 0px rgba(0,0,0,0.1)' }}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Post'}
        </button>
      </div>

      {/* Version selector */}
      {versions.length > 1 && (
        <div className="mb-5">
          <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Versions
          </p>
          <div className="space-y-1.5">
            {versions.map((v, idx) => (
              <button
                key={idx}
                onClick={() => onSelectVersion(idx)}
                className="w-full p-3 rounded-xl text-left text-xs font-medium transition-all border-2"
                style={{
                  borderColor: selectedVersion === idx ? '#63a5d3' : '#e2e8f0',
                  backgroundColor: selectedVersion === idx ? '#e3f0f7' : '#f8fafc',
                  color: selectedVersion === idx ? '#1e3a5f' : '#475569',
                }}
              >
                <span className="font-bold mr-1">{idx + 1}.</span>{v.hook || v.body?.slice(0, 80)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LinkedIn Card */}
      <div className="flex-1 overflow-y-auto">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
          {/* Profile row */}
          <div className="p-4 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white text-base font-bold"
                style={{ background: 'linear-gradient(135deg, #63a5d3, #2d5a7a)' }}
              >
                {(userName || 'Y')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 leading-tight">{userName || 'Your Name'}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-tight max-w-[180px] truncate">{userRole || 'Your headline'}</p>
                <p className="text-xs text-slate-400 mt-0.5">Just now · 🌐</p>
              </div>
            </div>
            <MoreHorizontal className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
          </div>

          {/* Post text */}
          <div className="px-4 pb-3">
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
              {renderText(fullContent)}
            </p>
            {isLong && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs font-semibold mt-1"
                style={{ color: '#0a66c2' }}
              >
                {expanded ? 'Show less' : '…see more'}
              </button>
            )}
          </div>

          {/* Image */}
          {images.length > 0 && (
            <div className="mx-4 mb-3 rounded-xl overflow-hidden border border-slate-100">
              <img src={images[0]} alt="" className="w-full max-h-48 object-cover" />
            </div>
          )}

          {/* Reactions */}
          <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <span>👍❤️</span>
              <span className="ml-1">34</span>
            </div>
            <span className="text-xs text-slate-400">5 comments</span>
          </div>

          {/* Action bar */}
          <div className="px-2 pb-2 pt-1 grid grid-cols-4">
            {[
              { icon: <ThumbsUp className="w-4 h-4" />, label: 'Like' },
              { icon: <MessageCircle className="w-4 h-4" />, label: 'Comment' },
              { icon: <Repeat2 className="w-4 h-4" />, label: 'Repost' },
              { icon: <Send className="w-4 h-4" />, label: 'Send' },
            ].map(({ icon, label }) => (
              <button key={label} className="flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 transition-colors">
                {icon}
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const WritingPage = () => {
  const navigate = useNavigate();
  const { user, hasLifetimeAccess } = useAuth();

  // State
  const [formData, setFormData] = useState(null);
  const [inspirationText, setInspirationText] = useState('');
  const [originalPostText, setOriginalPostText] = useState(null); // Store original post for tone reference
  const [isAnalyzingPost, setIsAnalyzingPost] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [identityText, setIdentityText] = useState('');
  const [identityCollapsed, setIdentityCollapsed] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [flowNodes, setFlowNodes] = useState([]);
  const [aiLoadingNode, setAiLoadingNode] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingType, setEditingType] = useState(null); // 'shorter' | 'punchier' | null
  const [selectedVersionIdx, setSelectedVersionIdx] = useState(0);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [showGenerated, setShowGenerated] = useState(false);
  const [showAiHelpModal, setShowAiHelpModal] = useState(false);
  const [aiHelpRequest, setAiHelpRequest] = useState('');
  const [showPollModal, setShowPollModal] = useState(false);
  const [hasSeenPoll, setHasSeenPoll] = useState(false);
  const [mobileTab, setMobileTab] = useState('editor'); // 'settings' | 'editor'
  const pollTimeoutRef = useRef(null);

  // --- Post generation controls ---
  const [postLength, setPostLength] = useState('medium');
  const [postTone, setPostTone] = useState('storyteller');
  const [includeEmojis, setIncludeEmojis] = useState(false);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeCTA, setIncludeCTA] = useState(true);
  const [includeHook, setIncludeHook] = useState(true);

  // --- Stripe Paywall State ---
  const [showCheckoutSidebar, setShowCheckoutSidebar] = useState(false); // Stripe checkout sidebar for writing tool
  const [paywallActive, setPaywallActive] = useState(false); // When true, block further actions and show sidebar on copy
  const [hasUsedFreeCopy, setHasUsedFreeCopy] = useState(false); // Track first free copy
  const [pricingTableBlocked, setPricingTableBlocked] = useState(false); // Stripe pricing table UI blocked/unavailable
  const [redirectCheckoutLoading, setRedirectCheckoutLoading] = useState(null); // "base" | "pro" | null
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'trialing' | 'active' | 'free' | etc.
  const pricingTableRef = useRef(null);

  // One-time trial-ended banner (toast) control
  useEffect(() => {
    const maybeShowTrialEndedNotice = async () => {
      if (!user) return;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) return;

        const res = await fetch(`${API_BASE}/api/user/status`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) return;

        const data = await res.json();
        const {
          subscription_status,
          stripe_subscription_id,
          writing_tool_trial_expires_at,
        } = data;

        setSubscriptionStatus(subscription_status || null);

        if (
          subscription_status === 'active' &&
          stripe_subscription_id &&
          writing_tool_trial_expires_at
        ) {
          const trialEnd = new Date(writing_tool_trial_expires_at);

          if (Number.isNaN(trialEnd.getTime())) return;

          const now = new Date();
          if (now >= trialEnd) {
            const storageKey = `writing_trial_notice_seen_${stripe_subscription_id}`;
            if (!localStorage.getItem(storageKey)) {
              const formattedDate = trialEnd.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              });

              toast.info(
                `Your 2-day free trial ended on ${formattedDate}. Your subscription is now active.`,
              );

              localStorage.setItem(storageKey, 'true');
            }
          }
        }
      } catch (err) {
        console.error('Error checking trial status for banner:', err);
      }
    };

    maybeShowTrialEndedNotice();
  }, [user]);

  // Load Stripe Pricing Table script and detect blockers when sidebar opens
  useEffect(() => {
    if (!showCheckoutSidebar) return;

    setPricingTableBlocked(false);

    // If the custom element is not defined, try loading the script
    const isPricingTableDefined = !!window.customElements?.get?.('stripe-pricing-table');
    if (!isPricingTableDefined) {
      // Avoid adding duplicate scripts
      const existing = document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/pricing-table.js';
        script.async = true;
        script.onerror = () => {
          setPricingTableBlocked(true);
        };
        document.body.appendChild(script);
      }
    }

    // After a short delay, if the element still isn't defined, assume blocked
    const t = setTimeout(() => {
      const definedNow = !!window.customElements?.get?.('stripe-pricing-table');
      if (!definedNow) {
        setPricingTableBlocked(true);
      }
    }, 1500);

    return () => clearTimeout(t);
  }, [showCheckoutSidebar]);

  const startRedirectCheckout = async (plan) => {
    try {
      setRedirectCheckoutLoading(plan);
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/billing/create-checkout-session?ui_mode=redirect`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ plan }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || `Checkout error (${res.status})`);
      }

      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('No checkout URL returned.');
    } catch (e) {
      toast.error(e.message || 'Could not start checkout. Please try again.');
    } finally {
      setRedirectCheckoutLoading(null);
    }
  };

  // If paywall is active and the sidebar is closed, clicking any button should re-open it.
  const handlePaywallClickCapture = (e) => {
    if (!paywallActive || showCheckoutSidebar) return;
    const btn = e.target?.closest?.('button');
    if (!btn) return;
    setShowCheckoutSidebar(true);
    e.preventDefault();
    e.stopPropagation();
  };

  // Load user data via backend API (same as RoadmapPage)
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      try {
        // Get auth token
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        const headers = token
          ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
          : { 'Content-Type': 'application/json' };

        // Fetch latest roadmap from backend API
        const endpoint = token
          ? `${API_BASE}/api/roadmaps/latest`
          : `${API_BASE}/api/roadmaps/dev-latest`;

        const response = await fetch(endpoint, { headers });

        if (response.ok) {
          const result = await response.json();
          const roadmapData = result.roadmap;

          if (roadmapData?.form_data) {
            setFormData(roadmapData.form_data);
            // Generate identity text from user data
            const identityBullets = generateIdentityText(roadmapData.form_data);
            setIdentityText(identityBullets);
          }
        } else {
        }
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    };

    loadUserData();
  }, [user]);

  // Helper: format an education entry (could be object {degree, institution, year} or string)
  const formatEducation = (edu) => {
    if (typeof edu === 'string') return edu;
    if (typeof edu === 'object' && edu !== null) {
      const parts = [];
      if (edu.degree) parts.push(edu.degree);
      if (edu.institution) parts.push(`from ${edu.institution}`);
      if (edu.year) parts.push(`(${edu.year})`);
      return parts.join(' ') || null;
    }
    return null;
  };

  // Helper: safely format a value that could be string, array, or object
  const formatField = (val, maxItems = 5) => {
    if (!val) return null;
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (Array.isArray(val) && val.length > 0) {
      return val.slice(0, maxItems).map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
    }
    return null;
  };

  // Generate identity text from formData
  const generateIdentityText = (data) => {
    if (!data) return '';


    const bullets = [];
    const resume = data.resume_data || {};
    const worldModel = data.world_model || {};
    const conversation = data.conversation || [];
    const isConversationArray = Array.isArray(conversation);
    const isChatFormat = isConversationArray && conversation.length > 0 && conversation[0]?.role != null;
    const isQAFormat = isConversationArray && conversation.length > 0 && conversation[0]?.question != null;

    // ═══════════════════════════════════════
    // SECTION 1: PROFILE BASICS
    // ═══════════════════════════════════════

    // Name — resume uses full_name, not name
    const name = resume.full_name || resume.name || worldModel.name;
    if (name) bullets.push(`• ${name}`);

    // Current role/title
    const currentRole = resume.current_title || resume.current_role || resume.title || worldModel.current_role;
    if (currentRole) bullets.push(`• Currently: ${currentRole}`);

    // Location
    const location = resume.location || worldModel.location;
    if (location) bullets.push(`• Based in: ${location}`);

    // Years of experience
    if (resume.years_of_experience) {
      bullets.push(`• Experience: ${resume.years_of_experience} years`);
    }

    // ═══════════════════════════════════════
    // SECTION 2: EDUCATION
    // ═══════════════════════════════════════

    const education = resume.education;
    if (education) {
      if (Array.isArray(education) && education.length > 0) {
        education.slice(0, 2).forEach(edu => {
          const formatted = formatEducation(edu);
          if (formatted) bullets.push(`• Education: ${formatted}`);
        });
      } else {
        const formatted = formatEducation(education);
        if (formatted) bullets.push(`• Education: ${formatted}`);
      }
    }

    // ═══════════════════════════════════════
    // SECTION 3: SKILLS & INDUSTRIES
    // ═══════════════════════════════════════

    // Skills — check resume.all_skills (primary), resume.skills, top-level data.skills
    const skills = formatField(resume.all_skills, 8)
      || formatField(resume.skills, 8)
      || formatField(data.skills, 8)
      || formatField(worldModel.skills, 8);
    if (skills) bullets.push(`• Skills: ${skills}`);

    // Soft skills
    const softSkills = formatField(resume.soft_skills, 5);
    if (softSkills) bullets.push(`• Soft Skills: ${softSkills}`);

    // Industries
    const industries = formatField(resume.industries, 4)
      || formatField(worldModel.industries, 4)
      || formatField(data.interests, 4);
    if (industries) bullets.push(`• Industries: ${industries}`);

    // ═══════════════════════════════════════
    // SECTION 4: CAREER HISTORY
    // ═══════════════════════════════════════

    // Recent roles
    const recentRoles = formatField(resume.recent_roles, 4);
    if (recentRoles) bullets.push(`• Recent Roles: ${recentRoles}`);

    // Companies
    const companies = formatField(resume.companies, 4);
    if (companies) bullets.push(`• Companies: ${companies}`);

    // Career highlights
    if (Array.isArray(resume.career_highlights) && resume.career_highlights.length > 0) {
      resume.career_highlights.slice(0, 3).forEach(h => {
        if (typeof h === 'string' && h.trim()) {
          const truncated = h.length > 120 ? h.slice(0, 117) + '...' : h;
          bullets.push(`• Highlight: ${truncated}`);
        }
      });
    }

    // Professional summary
    if (resume.summary && typeof resume.summary === 'string' && resume.summary.length > 20) {
      const summary = resume.summary.length > 200 ? resume.summary.slice(0, 197) + '...' : resume.summary;
      bullets.push(`• Summary: ${summary}`);
    }

    // ═══════════════════════════════════════
    // SECTION 5: INTERESTS & GOALS
    // ═══════════════════════════════════════

    // YouTube history
    const youtube = data.youtube_history || resume.youtube || worldModel.youtube_history;
    if (youtube && Array.isArray(youtube) && youtube.length > 0) {
      const ytTopics = youtube.slice(0, 3).map(y => typeof y === 'string' ? y : y?.topic || y?.title || '').filter(Boolean);
      if (ytTopics.length > 0) bullets.push(`• Watches content about: ${ytTopics.join(', ')}`);
    }

    // Top-level interests/experiences from FigChat formData
    if (data.interests && typeof data.interests === 'string' && data.interests.length > 3) {
      if (!industries || !industries.includes(data.interests)) {
        bullets.push(`• Passionate about: ${data.interests}`);
      }
    }
    if (data.experiences && typeof data.experiences === 'string' && data.experiences.length > 20) {
      const exp = data.experiences.length > 200 ? data.experiences.slice(0, 197) + '...' : data.experiences;
      if (!resume.summary) bullets.push(`• Background: ${exp}`);
    }

    // ═══════════════════════════════════════
    // SECTION 6: AI INTERN CONVERSATION
    // ═══════════════════════════════════════
    // FigChat saves conversation as [{role: 'user'/'agent', content: '...'}]

    if (isChatFormat) {
      const chatMessages = conversation.filter(m =>
        m.content && typeof m.content === 'string' && m.content.trim().length > 0
      );

      // Get user's own words (what they told the AI intern)
      const userMessages = chatMessages
        .filter(m => m.role === 'user')
        .map(m => m.content.trim())
        .filter(c => c.length > 8 && !c.startsWith('📄')); // Skip emoji-prefix system messages

      // Get agent insights about the user (the AI intern's observations)
      const agentMessages = chatMessages
        .filter(m => m.role === 'agent' || m.role === 'assistant')
        .map(m => m.content.trim())
        .filter(c => c.length > 20);

      if (userMessages.length > 0 || agentMessages.length > 0) {
        bullets.push('');
        bullets.push('── AI Intern Conversation ──');

        // Include what the user said (their words reveal personality, goals, values)
        if (userMessages.length > 0) {
          userMessages.slice(0, 6).forEach(msg => {
            const truncated = msg.length > 180 ? msg.slice(0, 177) + '...' : msg;
            bullets.push(`• You said: "${truncated}"`);
          });
        }

        // Include key agent observations (they contain analyzed insights)
        if (agentMessages.length > 0) {
          // Pick agent messages that seem like insights/observations (longer, substantive ones)
          const insightful = agentMessages
            .filter(msg => msg.length > 50)
            .slice(-3); // Take the last ones which are usually more insightful/summary-like

          insightful.forEach(msg => {
            const truncated = msg.length > 200 ? msg.slice(0, 197) + '...' : msg;
            bullets.push(`• Fig observed: "${truncated}"`);
          });
        }
      }
    }

    // Also include chat_context string if available and conversation array was empty/missing
    if (data.chat_context && typeof data.chat_context === 'string' && data.chat_context.length > 30 && !isChatFormat) {
      bullets.push('');
      bullets.push('── AI Intern Conversation ──');
      const lines = data.chat_context.split('\n').filter(l => l.trim());
      
      // Include both user and agent lines
      lines.slice(0, 12).forEach(line => {
        const trimmed = line.trim();
        if (trimmed.length > 10) {
          const truncated = trimmed.length > 180 ? trimmed.slice(0, 177) + '...' : trimmed;
          bullets.push(`• ${truncated}`);
        }
      });
    }

    // ═══════════════════════════════════════
    // SECTION 7: Q&A ONBOARDING (if used)
    // ═══════════════════════════════════════

    if (isQAFormat) {
      const qaInsights = conversation.filter(qa => qa.answer && String(qa.answer).length > 5);
      if (qaInsights.length > 0) {
        bullets.push('');
        bullets.push('── Onboarding Q&A ──');
        qaInsights.forEach(qa => {
          const q = String(qa.question);
          const a = String(qa.answer);
          const qTrunc = q.length > 80 ? q.slice(0, 77) + '...' : q;
          const aTrunc = a.length > 120 ? a.slice(0, 117) + '...' : a;
          bullets.push(`• Q: ${qTrunc}`);
          bullets.push(`  A: ${aTrunc}`);
        });
      }
    }

    return bullets.join('\n');
  };

  // Get user context for AI
  const getUserContext = useCallback(() => {
    if (!formData) return {};
    const resume = formData.resume_data || {};

    return {
      name: resume.full_name || resume.name || '',
      role: resume.current_title || resume.current_role || '',
      location: resume.location || '',
      education: formatEducation(Array.isArray(resume.education) ? resume.education[0] : resume.education) || '',
      interests: formData.interests || formatField(resume.industries, 3) || '',
      industries: resume.industries?.slice(0, 3) || [],
      skills: resume.all_skills?.slice(0, 5) || resume.skills?.slice(0, 5) || [],
      experience: resume.recent_roles?.slice(0, 3) || [],
      youtube: formData.youtube_history?.slice(0, 3) || [],
      identity: identityText
    };
  }, [formData, identityText]);

  // Initialize a template
  const initializeFramework = useCallback((templateId) => {
    const template = TEMPLATES[templateId];
    if (!template) return;

    const context = getUserContext();
    const nodes = template.nodes.map(node => {
      let prefillContent = '';
      let prefilled = false;

      if (node.id === 'belief' && context.interests) {
        prefillContent = `I used to think ${context.interests.toLowerCase()} was the only path forward.`;
        prefilled = true;
      } else if (node.id === 'setup' && context.role) {
        prefillContent = `When I was working as a ${context.role}...`;
        prefilled = true;
      } else if (node.id === 'application' && context.industries.length > 0) {
        prefillContent = `For anyone in ${context.industries[0]}...`;
        prefilled = true;
      }

      return { ...node, content: prefillContent, prefilled };
    });

    setFlowNodes(nodes);
    setSelectedFramework(templateId);
    setOriginalPostText(null);
    setShowGenerated(false);
    setGeneratedPost(null);
    setMobileTab('editor'); // go to editor panel securely
  }, [getUserContext]);

  // Analyze pasted LinkedIn post and generate custom framework
  const handleAnalyzePost = async () => {
    if (!inspirationText.trim()) return;

    // Require at least starting the Stripe free trial (trialing/active) before
    // allowing users to use the Generate Framework AI endpoint.
    // If they haven't started the trial, always show the paywall sidebar.
    if (subscriptionStatus !== 'trialing' && subscriptionStatus !== 'active') {
      setShowCheckoutSidebar(true);
      return;
    }
    
    setIsAnalyzingPost(true);
    toast.loading('Analyzing post structure and personalizing content...', { id: 'analyze-post' });

    try {
      const response = await fetch(`${API_BASE}/api/analyze-post-structure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_text: inspirationText,
          user_id: user?.id,
          user_identity: identityText || '' // Pass user's identity for personalization
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        const msg = errData?.detail || `Server error (${response.status})`;
        throw new Error(msg);
      }

      const data = await response.json();
      
      // Set the custom framework from the analyzed post
      // The API now returns pre-filled content for each node
      const customNodes = data.nodes.map(node => ({
        ...node,
        content: node.content || '',
        prefilled: !!node.content,
      }));

      setFlowNodes(customNodes);
      setSelectedFramework(`custom:${data.framework_name}`);
      setOriginalPostText(inspirationText); // Store original post for tone reference when generating
      setShowGenerated(false);
      setGeneratedPost(null);
      setMobileTab('editor'); // Switch tab on mobile automatically
      toast.success(`Framework "${data.framework_name}" created with ${customNodes.length} sections!`, { id: 'analyze-post' });
    } catch (err) {
      console.error('Error analyzing post:', err);
      toast.error(err.message || 'Failed to analyze post structure. Please try again.', { id: 'analyze-post' });
    } finally {
      setIsAnalyzingPost(false);
    }
  };

  // Handle node updates
  const handleNodeUpdate = (nodeId, content) => {
    setFlowNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, content, prefilled: false } : node
    ));
  };

  const handleNodeDelete = (nodeId) => {
    setFlowNodes(prev => prev.filter(node => node.id !== nodeId));
  };

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      };
    }
    return { 'Content-Type': 'application/json' };
  };

  // Triggered when user copies the generated post
  const handleGeneratedPostCopy = () => {
    // First copy is free
    if (!hasUsedFreeCopy) {
      setHasUsedFreeCopy(true);
      return;
    }
    // After first copy (and especially after paywallActive), open the Stripe sidebar
    setPaywallActive(true);
    setShowCheckoutSidebar(true);
  };

  const handleAiHelp = async (nodeId) => {
    const node = flowNodes.find(n => n.id === nodeId);
    if (!node) return;

    setAiLoadingNode(nodeId);
    const context = getUserContext();

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/writing-agent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: `Generate compelling content for a LinkedIn post section called "${node.label}".
          The prompt is: "${node.prompt}"

          About the author:
          ${context.identity}

          Keep it personal, authentic, and under 50 words. Write in first person.`,
          conversation_history: [],
          user_id: user?.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        handleNodeUpdate(nodeId, data.response);
        toast.success('Content generated!');
      } else if (response.status === 402) {
        if (hasLifetimeAccess) return;
        // Payment required – activate paywall and show Stripe sidebar
        setPaywallActive(true);
        setShowCheckoutSidebar(true);
      }
    } catch (err) {
      toast.error('Failed to generate content');
    } finally {
      setAiLoadingNode(null);
    }
  };

  const handleAddNode = () => {
    const newNode = {
      id: `custom-${Date.now()}`,
      label: 'Custom Section',
      prompt: 'Add your own content here...',
      content: '',
      prefilled: false,
      row: flowNodes.length,
      col: 0
    };
    setFlowNodes(prev => [...prev, newNode]);
  };

  const allNodesFilled = flowNodes.length > 0 && flowNodes.every(node => node.content.trim());

  const handleGeneratePost = async () => {
    if (!allNodesFilled) return;

    setIsGenerating(true);
    toast.loading('Generating your LinkedIn post...', { id: 'generate-post' });

    try {
      const headers = await getAuthHeaders();
      const nodeContents = flowNodes
        .sort((a, b) => a.row - b.row || a.col - b.col)
        .map(node => `[${node.label}]: ${node.content || ''}`)
        .filter(c => c.trim())
        .join('\n\n');

      const context = getUserContext();

      // Template blueprint
      const template = TEMPLATES[selectedFramework];
      const blueprint = template?.promptBlueprint || 'Write a compelling LinkedIn post.';

      // Control instructions
      const wordTarget = { short: '~100 words', medium: '~200 words', long: '~300 words' }[postLength] || '~200 words';
      const toneLabel = TONES.find(t => t.id === postTone)?.label || postTone;
      const hookInstr = includeHook
        ? 'Open with a strong hook — first line creates curiosity, tension, or a bold claim that makes people stop scrolling.'
        : 'Open with a clear, direct statement.';
      const emojiInstr = includeEmojis
        ? 'Use emojis sparingly and naturally (1–3 total, only where they add tone).'
        : 'Do NOT use any emojis.';
      const hashtagInstr = includeHashtags
        ? 'End with 3–5 relevant hashtags on their own line (e.g. #career #leadership #growthmindset).'
        : 'Do NOT include hashtags.';
      const ctaInstr = includeCTA
        ? 'End the body with a compelling call-to-action — a question, invitation to comment, or reason to follow.'
        : 'Do NOT add a call-to-action.';

      // Reference-post path or template path
      // Shared hard rules injected into every generation path
      const HARD_RULES = `HARD RULES — breaking any of these makes the post sound AI-generated:
THINGS TO NEVER WRITE:
- Em dashes (—). Use a period, comma, or line break instead.
- The staccato triple-fragment pattern: "No X. No Y. Just Z." or "No degree. No connections. Just results." — this is one of the most recognizable AI signatures. Use 2 items or rewrite as a full sentence.
- Closing moral announcements: "The lesson:", "The takeaway:", "The real lesson here is", "What I learned:", "Here's the thing:"
- Abstract-noun wisdom openers: "Leadership is...", "Success requires...", "True growth means..." — name a specific situation instead.
- Buzzwords: leverage, game-changer, paradigm shift, innovative, cutting-edge, impactful (as an adjective), synergy, ecosystem (unless literally about nature).
- Filler transitions: Moreover, Furthermore, In conclusion, In summary, Additionally.
- Clichéd openers: "In today's world,", "What nobody tells you,", "At the end of the day,", "Here's what I've learned:".
- The "it's not about X, it's about Y" formula as a closing line — it reads as a manufactured epiphany.
- Perfectly symmetrical problem → insight → lesson arcs where every paragraph is the same length.

THINGS TO ALWAYS DO:
- Use contractions throughout: I'm, it's, didn't, couldn't, they're, you've.
- Name specific details: "three users" not "early adopters", "6am on a Tuesday" not "early mornings". Specificity is what makes writing feel real.
- Vary sentence length dramatically. Mostly short (5–10 words). Occasionally medium. Rarely long. Never the same length twice in a row.
- Hit Enter more than feels comfortable. White space is part of the voice.
- Let some ideas feel unresolved — not every post needs a perfect bow. A sharp final observation beats a wrapped-up lesson.
- Hashtags must reflect the specific topics in this post, not the user's general background.`;

      const JSON_FORMAT = `Generate 3 complete post variations. Each version should open with a different hook AND have a body that flows naturally from that specific hook. Same core message, three distinct takes — vary the angle, emphasis, or opening approach, not just the first sentence.

Return ONLY valid JSON:
{
  "versions": [
    { "hook": "opening line for version 1", "body": "rest of post for version 1 (no opening line, hashtags at end if requested)" },
    { "hook": "opening line for version 2", "body": "rest of post for version 2" },
    { "hook": "opening line for version 3", "body": "rest of post for version 3" }
  ]
}`;

      let analysisPrompt = '';
      if (originalPostText) {
        analysisPrompt = `You are a ghostwriter who specializes in making LinkedIn posts sound like a specific human wrote them. Follow this 3-step process:

STEP 1 — READ THE REFERENCE POST:
"""
${originalPostText}
"""
Study it like a copywriter would. Note:
- Typical sentence length (count the words in a few sentences — is it 6 words? 12? 20?)
- Vocabulary register: does this person use simple everyday words or more formal language?
- Paragraph length: one sentence? two? more?
- How the post opens: question, bold claim, scene, number, confession?
- How the post closes: question, observation, challenge, quiet statement?
- Contractions: does the writer use them? how often?
- Detail density: do they name specific numbers, dates, names — or stay general?
- Transition style: abrupt cuts between ideas, or connective tissue?

STEP 2 — BUILD A VOICE FINGERPRINT:
Write a short voice fingerprint that captures HOW this person writes — not what they write about. Example: "Short sentences averaging 8 words. Casual vocabulary, lots of contractions. One-sentence paragraphs. Opens with a provocative question. Closes with a quiet observation, not a lesson. Specific numbers and details throughout. Transitions are abrupt — no connective words."

STEP 3 — WRITE A NEW POST:
Write a new post for this person using ONLY their content below. The post must match the voice fingerprint exactly — same sentence length pattern, same vocabulary register, same paragraph density, same opening and closing style. Do NOT copy specific facts or phrases from the reference post. But DO write as if the exact same human wrote it.

USER INFO:
${context.identity || ''}

RAW CONTENT:
${nodeContents}

STYLE RULES:
- Tone: ${toneLabel}
- Length: ${wordTarget}
- ${hookInstr}
- ${emojiInstr}
- ${hashtagInstr}
- ${ctaInstr}

${HARD_RULES}

${JSON_FORMAT}`;
      } else {
        // Fetch a style example from the backend to use as structural guidance
        let styleExample = null;
        try {
          const styleRes = await fetch(
            `${API_BASE}/api/style-example?template_type=${encodeURIComponent(selectedFramework || '')}&tone=${encodeURIComponent(postTone || '')}`,
            { headers }
          );
          if (styleRes.ok) {
            const styleData = await styleRes.json();
            styleExample = styleData.post || null;
          }
        } catch (styleErr) {
          console.warn('Could not load style example, falling back to blueprint only:', styleErr);
        }

        if (styleExample) {
          analysisPrompt = `You are a ghostwriter who specializes in making LinkedIn posts sound like a specific human wrote them. Follow this 3-step process:

STEP 1 — READ THE STYLE REFERENCE:
The post below is a curated example for the "${template?.name || 'Post'}" format with a "${toneLabel}" tone.
"""
${styleExample}
"""
Study it like a copywriter would. Note:
- Typical sentence length (count the words in a few sentences — is it 6 words? 12? 20?)
- Vocabulary register: does this person use simple everyday words or more formal language?
- Paragraph length: one sentence? two? more?
- How the post opens: question, bold claim, scene, number, confession?
- How the post closes: question, observation, challenge, quiet statement?
- Contractions: does the writer use them? how often?
- Detail density: do they name specific numbers, dates, names — or stay general?
- Transition style: abrupt cuts between ideas, or connective tissue?
- Template structure to follow: ${blueprint}

STEP 2 — BUILD A VOICE FINGERPRINT:
Write a short voice fingerprint that captures HOW this person writes — not what they write about. Example: "Short sentences averaging 8 words. Casual vocabulary, lots of contractions. One-sentence paragraphs. Opens with a provocative question. Closes with a quiet observation, not a lesson. Specific numbers and details throughout. Transitions are abrupt — no connective words."

STEP 3 — WRITE A NEW POST:
Write a new post for this person using ONLY their content below. The post must match the voice fingerprint exactly — same sentence length pattern, same vocabulary register, same paragraph density, same opening and closing style. Do NOT copy specific facts or phrases from the style reference. But DO write as if the exact same kind of human wrote it.

USER INFO:
${context.identity || ''}

USER'S CONTENT (synthesize into a flowing post — do not just list sections):
${nodeContents}

STYLE RULES:
- Tone: ${toneLabel}
- Length: ${wordTarget}
- ${hookInstr}
- ${emojiInstr}
- ${hashtagInstr}
- ${ctaInstr}

${HARD_RULES}

${JSON_FORMAT}`;
        } else {
          // Fallback: blueprint-only (no style example available)
          analysisPrompt = `You are a ghostwriter who writes viral LinkedIn posts that sound like a real human wrote them — not like a content marketing team.

TEMPLATE: ${template?.name || 'Post'} — ${template?.subtitle || ''}
BLUEPRINT: ${blueprint}

TONE: ${toneLabel}
LENGTH: ${wordTarget}

FORMATTING:
- Short paragraphs (1–3 lines max). Line breaks between ideas.
- ${hookInstr}
- ${emojiInstr}
- ${hashtagInstr}
- ${ctaInstr}

USER BACKGROUND:
${context.identity || ''}

USER'S CONTENT (synthesize into a flowing post — do not just list sections):
${nodeContents}

${HARD_RULES}

${JSON_FORMAT}`;
        }
      }

      const response = await fetch(`${API_BASE}/api/writing-agent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: analysisPrompt,
          conversation_history: [],
          user_id: user?.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        try {
          // Strip markdown code fences if present
          let responseText = data.response.trim();
          if (responseText.startsWith('```')) {
            // Remove opening ```json or ```
            responseText = responseText.replace(/^```(?:json)?\s*\n?/, '');
            // Remove closing ```
            responseText = responseText.replace(/\n?```\s*$/, '');
            responseText = responseText.trim();
          }
          
          const parsed = JSON.parse(responseText);
          setGeneratedPost(parsed);
          setSelectedVersionIdx(prev => {
            const count = parsed.versions?.length || 1;
            return prev < count ? prev : 0;
          });
        } catch (parseError) {
          console.error('JSON parse error:', parseError, 'Response:', data.response);
          const responseText = data.response.trim();
          setGeneratedPost({
            versions: [{ hook: responseText.slice(0, 100), body: responseText }]
          });
          setSelectedVersionIdx(0);
        }
        setShowGenerated(true);
        toast.success('Post generated!', { id: 'generate-post' });
      } else if (response.status === 402) {
        if (hasLifetimeAccess) return;
        // Payment required – activate paywall and show Stripe sidebar
        setPaywallActive(true);
        setShowCheckoutSidebar(true);
      } else {
        const errData = await response.json().catch(() => null);
        const msg = errData?.detail || `Server error (${response.status})`;
        throw new Error(msg);
      }
    } catch (err) {
      console.error('Error generating post:', err);
      toast.error(err.message || 'Failed to generate post. Please try again.', { id: 'generate-post' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEditPost = async (editType) => {
    if (!generatedPost || editingType) return;
    const versions = generatedPost.versions || [];
    const current = versions[selectedVersionIdx];
    if (!current) return;
    setEditingType(editType);
    toast.loading('Editing...', { id: 'edit-post' });

    const instructions = {
      shorter: `Make this post shorter and less wordy. Cut fluff, redundant sentences, and anything that doesn't add meaning. Keep all the core ideas and substance — just say the same thing in fewer words. Preserve short lines and good spacing. Do not change the tone or structure.`,
      punchier: `Make the language punchier. Rules:
- Shorten sentences
- Remove filler words
- Increase contrast and tension where it already exists
- Use more direct, conversational phrasing
- Keep the meaning identical
- Keep the tone natural — not dramatic, not cringe
Do not add new ideas or change the structure.`,
    };

    const fullText = [current.hook, current.body].filter(Boolean).join('\n\n');

    try {
      const headers = await getAuthHeaders();
      const prompt = `You are editing a LinkedIn post. Apply ONLY the following instruction — change nothing else.

INSTRUCTION:
${instructions[editType]}

POST:
"""
${fullText}
"""

HARD RULES — do not introduce any of these while editing:
- No em dashes (—). Use a period, comma, or line break instead.
- No staccato triple-fragment pattern: "No X. No Y. Just Z." — use 2 items or rewrite as a full sentence.
- No closing moral announcements: "The lesson:", "The takeaway:", "What I learned:".
- No abstract-noun wisdom: "Leadership is...", "Success requires...".
- No buzzwords: leverage, game-changer, paradigm shift, innovative, cutting-edge.
- No filler transitions: Moreover, Furthermore, In conclusion, Additionally.
- Keep contractions — don't make it more formal.
- Keep specific details — don't generalize them.
- Keep hashtags (if any) relevant to the post topics.
- The first line is the opening hook. Return it separately from the body.

Return ONLY valid JSON:
{
  "hook": "opening line only",
  "body": "rest of the post (no opening line, hashtags at end if they existed)"
}`;

      const response = await fetch(`${API_BASE}/api/writing-agent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: prompt, conversation_history: [], user_id: user?.id, fast: true })
      });

      if (response.ok) {
        const data = await response.json();
        let text = data.response.trim();
        if (text.startsWith('```')) {
          text = text.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '').trim();
        }
        const edited = JSON.parse(text);
        const newVersions = versions.map((v, i) =>
          i === selectedVersionIdx ? { hook: edited.hook, body: edited.body } : v
        );
        setGeneratedPost({ versions: newVersions });
        toast.success('Done!', { id: 'edit-post' });
      } else {
        throw new Error('Edit failed');
      }
    } catch (err) {
      console.error('Error editing post:', err);
      toast.error('Failed to edit. Please try again.', { id: 'edit-post' });
    } finally {
      setEditingType(null);
    }
  };

  // (Old poll + Buy Me A Coffee flow removed; Stripe + subscription now handle monetization)

  const handlePollAnswer = (answer) => {
    setShowPollModal(false);
    setHasSeenPoll(true);
  };

  const handleImageUpload = (imageData) => {
    if (uploadedImages.length < 3) {
      setUploadedImages(prev => [...prev, imageData]);
    }
  };

  const handleImageRemove = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const action = e.dataTransfer.getData('action');
    if (action === 'pegman') {
      // Open modal for user to type their request instead of adding a node
      setShowAiHelpModal(true);
      setAiHelpRequest('');
    }
  };

  const handleAiHelpRequest = async () => {
    if (!aiHelpRequest.trim()) {
      toast.error('Please describe what you want to change');
      return;
    }

    setShowAiHelpModal(false);
    toast.loading('Processing your request...', { id: 'ai-help' });

    try {
      const context = getUserContext();
      const nodeContents = flowNodes
        .sort((a, b) => a.row - b.row || a.col - b.col)
        .map((node, idx) => `${idx + 1}. [${node.label}]: ${node.content || '(empty)'}`)
        .join('\n');

      const response = await fetch(`${API_BASE}/api/writing-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `The user wants to make changes to their LinkedIn post framework. Here's the current state:

CURRENT FRAMEWORK SECTIONS:
${nodeContents}

USER REQUEST:
"${aiHelpRequest}"

Based on the user's request, directly apply the changes. Return ONLY a JSON object in this exact format:
{
  "actions": [
    {
      "type": "edit" | "add" | "remove" | "reorder",
      "sectionIndex": 0,  // 0-based index of section (for edit/remove)
      "newContent": "updated content here",  // for edit/add
      "newLabel": "Section Label",  // for add
      "newPrompt": "Prompt text",  // for add
      "newOrder": [0, 1, 2]  // for reorder (array of indices in new order)
    }
  ]
}

If editing: update the content of the specified section.
If adding: add a new section at the specified position.
If removing: remove the specified section.
If reordering: rearrange sections according to newOrder array.

Return ONLY valid JSON, no explanations.`,
          conversation_history: [],
          user_id: user?.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        let responseText = data.response.trim();
        
        // Strip markdown code fences if present
        if (responseText.startsWith('```')) {
          responseText = responseText.replace(/^```(?:json)?\s*\n?/, '');
          responseText = responseText.replace(/\n?```\s*$/, '');
          responseText = responseText.trim();
        }

        const parsed = JSON.parse(responseText);
        
        // Apply the changes
        if (parsed.actions && Array.isArray(parsed.actions)) {
          let updatedNodes = [...flowNodes];
          
          parsed.actions.forEach(action => {
            if (action.type === 'edit' && action.sectionIndex !== undefined) {
              const sortedNodes = [...flowNodes].sort((a, b) => a.row - b.row || a.col - b.col);
              const nodeToEdit = sortedNodes[action.sectionIndex];
              if (nodeToEdit) {
                updatedNodes = updatedNodes.map(n => 
                  n.id === nodeToEdit.id 
                    ? { ...n, content: action.newContent || n.content }
                    : n
                );
              }
            } else if (action.type === 'add') {
              const newNode = {
                id: `custom-${Date.now()}`,
                label: action.newLabel || 'New Section',
                prompt: action.newPrompt || 'Add your content here...',
                content: action.newContent || '',
                prefilled: false,
                row: action.sectionIndex !== undefined ? action.sectionIndex : updatedNodes.length,
                col: 0
              };
              updatedNodes.push(newNode);
            } else if (action.type === 'remove' && action.sectionIndex !== undefined) {
              const sortedNodes = [...flowNodes].sort((a, b) => a.row - b.row || a.col - b.col);
              const nodeToRemove = sortedNodes[action.sectionIndex];
              if (nodeToRemove) {
                updatedNodes = updatedNodes.filter(n => n.id !== nodeToRemove.id);
              }
            } else if (action.type === 'reorder' && action.newOrder) {
              const sortedNodes = [...flowNodes].sort((a, b) => a.row - b.row || a.col - b.col);
              updatedNodes = action.newOrder.map((newIdx, pos) => {
                const node = sortedNodes[newIdx];
                if (node) {
                  return { ...node, row: pos, col: 0 };
                }
                return node;
              }).filter(Boolean);
            }
          });
          
          setFlowNodes(updatedNodes);
          toast.success('Changes applied!', { id: 'ai-help' });
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Failed to process request');
      }
    } catch (err) {
      console.error('Error processing AI help request:', err);
      toast.error('Failed to process your request. Please try again.', { id: 'ai-help' });
    } finally {
      setAiHelpRequest('');
    }
  };

  const nodesByRow = flowNodes.reduce((acc, node) => {
    if (!acc[node.row]) acc[node.row] = [];
    acc[node.row].push(node);
    return acc;
  }, {});

  const context = getUserContext();

  return (
    <div
      className="min-h-screen"
      onClickCapture={handlePaywallClickCapture}
      style={{
        backgroundColor: '#FAF8F5',
        backgroundImage: `
          linear-gradient(rgba(0,0,0,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.025) 1px, transparent 1px)
        `,
        backgroundSize: '24px 24px'
      }}
    >
      {/* Back Button Only - No Header */}
      <div className="fixed top-6 left-6 z-50">
        <button
          onClick={() => navigate('/roadmap')}
          className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-white rounded-xl text-slate-600 hover:text-slate-900 font-bold text-xs sm:text-sm transition-all border-2 border-slate-200 hover:border-slate-300"
          style={{ boxShadow: '4px 4px 0px rgba(0,0,0,0.06)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </button>
      </div>

      {/* Mobile Toggle Tabs */}
      <div className="lg:hidden flex items-center justify-center fixed top-6 left-0 right-0 z-40 pointer-events-none">
        <div className="bg-white p-1 rounded-2xl border-2 border-slate-200 pointer-events-auto flex items-center justify-center shadow-[4px_4px_0px_rgba(0,0,0,0.05)] ml-16" style={{ width: 'auto' }}>
          <button
            onClick={() => setMobileTab('editor')}
            className={`py-1.5 px-4 rounded-xl text-xs font-bold transition-all ${
              mobileTab === 'editor'
                ? 'bg-slate-800 text-white'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            1. Write
          </button>
          <button
            onClick={() => setMobileTab('settings')}
            className={`py-1.5 px-4 rounded-xl text-xs font-bold transition-all ${
              mobileTab === 'settings'
                ? 'bg-slate-800 text-white'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            2. Setup
          </button>
        </div>
      </div>

      {/* Main 3-Panel Layout */}
      <div className="flex flex-col lg:flex-row h-screen w-full overflow-hidden">

        {/* CENTER PANEL - Canvas or Preview */}
        <main
          className={`${mobileTab === 'editor' ? 'flex flex-col' : 'hidden'} lg:flex lg:flex-col flex-1 overflow-y-auto p-4 lg:p-8 pt-24 lg:pt-20 pb-20 w-full`}
          onDrop={!showGenerated ? handleCanvasDrop : undefined}
          onDragOver={!showGenerated ? (e) => e.preventDefault() : undefined}
        >
          {/* Preview Panel — replaces canvas when post is generated */}
          {showGenerated && generatedPost ? (
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <button
                  onClick={() => setShowGenerated(false)}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl text-slate-600 hover:text-slate-900 font-bold text-sm transition-all border-2 border-slate-200 hover:border-slate-300"
                  style={{ boxShadow: '4px 4px 0px rgba(0,0,0,0.06)' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to editor
                </button>
              </div>
              <LinkedInPreview
                versions={generatedPost.versions || [{ hook: '', body: generatedPost.body || '' }]}
                images={uploadedImages}
                onCopy={handleGeneratedPostCopy}
                isVisible={true}
                userName={context.name}
                userRole={context.role}
                paywallActive={paywallActive}
                selectedVersion={selectedVersionIdx}
                onSelectVersion={setSelectedVersionIdx}
              />
              <div className="mt-4 flex gap-3">
                {[
                  { key: 'shorter', label: 'Make shorter' },
                  { key: 'punchier', label: 'Make punchier' },
                ].map(({ key, label }) => {
                  const isActive = editingType === key;
                  const isDisabled = !!editingType;
                  return (
                    <button
                      key={key}
                      onClick={() => handleEditPost(key)}
                      disabled={isDisabled}
                      className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl text-slate-700 font-bold text-sm border-2 border-slate-200 hover:border-slate-400 hover:text-slate-900 transition-all disabled:opacity-40 disabled:cursor-wait"
                      style={{ boxShadow: '3px 3px 0px rgba(0,0,0,0.06)' }}
                    >
                      {isActive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (

          <div className="max-w-3xl mx-auto">

            {/* Framework Selector OR Loading State */}
            {!selectedFramework && (
              <div className="text-center py-16">
                {isAnalyzingPost ? (
                  /* Loading state while framework is being generated */
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center"
                  >
                    <div className="relative w-24 h-24 mx-auto mb-8">
                      <div className="absolute inset-0 rounded-3xl border-3 animate-pulse"
                        style={{
                          borderWidth: '3px',
                          background: 'linear-gradient(to bottom right, #e3f0f7, #f0f7fb)',
                          borderColor: '#a8d0e8',
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#63a5d3' }} />
                      </div>
                    </div>
                    <h2 className="text-2xl font-black text-slate-800 mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Analyzing Post Structure...
                    </h2>
                    <p className="text-sm font-medium text-slate-500 mb-6 max-w-sm mx-auto">
                      Breaking down the writing framework, extracting key sections and content
                    </p>
                    <div className="flex gap-2 justify-center">
                      {[0, 1, 2, 3, 4].map(i => (
                        <motion.div
                          key={i}
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: '#63a5d3' }}
                          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  /* Template picker */
                  <>
                <h2 className="text-3xl font-black text-slate-800 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Choose a Template
                </h2>
                <p className="text-sm font-medium text-slate-500 mb-10 max-w-md mx-auto">
                  Or paste a LinkedIn post on the left to generate a custom framework
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-2xl mx-auto">
                  {Object.entries(TEMPLATES).map(([id, tmpl]) => (
                    <button
                      key={id}
                      onClick={() => initializeFramework(id)}
                      className="p-4 bg-white border-2 border-slate-200 rounded-2xl text-left transition-all group"
                      style={{ boxShadow: '4px 4px 0px rgba(0,0,0,0.05)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#63a5d3';
                        e.currentTarget.style.backgroundColor = '#f0f7fb';
                        e.currentTarget.style.boxShadow = '6px 6px 0px rgba(99,165,211,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.backgroundColor = '#ffffff';
                        e.currentTarget.style.boxShadow = '4px 4px 0px rgba(0,0,0,0.05)';
                      }}
                    >
                      <div className="text-2xl mb-2">{tmpl.emoji}</div>
                      <div className="text-sm font-black text-slate-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>{tmpl.name}</div>
                      <div className="text-xs font-medium text-slate-500 mt-0.5">{tmpl.subtitle}</div>
                    </button>
                  ))}
                </div>
                  </>
                )}
              </div>
            )}

            {/* Flow Canvas */}
            {selectedFramework && (
              <>
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <h2 className="text-2xl font-black text-slate-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {selectedFramework.startsWith('custom:')
                        ? selectedFramework.replace('custom:', '')
                        : `${TEMPLATES[selectedFramework]?.emoji || ''} ${TEMPLATES[selectedFramework]?.name || ''}`}
                    </h2>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                      {flowNodes.filter(n => n.content.trim()).length} / {flowNodes.length} sections filled
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleAddNode}
                      className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
                      style={{ boxShadow: '3px 3px 0px rgba(0,0,0,0.05)' }}
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setSelectedFramework(null);
                        setFlowNodes([]);
                        setOriginalPostText(null);
                        setShowGenerated(false);
                      }}
                      className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {Object.entries(nodesByRow)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([row, nodes], rowIdx) => (
                      <div key={row}>
                        {rowIdx > 0 && (
                          <div className="flex justify-center">
                            <NodeConnector />
                          </div>
                        )}
                        <div className="flex items-center justify-center gap-4">
                          {nodes.map((node, nodeIdx) => (
                            <React.Fragment key={node.id}>
                              {nodeIdx > 0 && <NodeConnector direction="right" />}
                              <FlowNode
                                node={node}
                                onUpdate={handleNodeUpdate}
                                onDelete={handleNodeDelete}
                                onAiHelp={handleAiHelp}
                                isAiLoading={aiLoadingNode === node.id}
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>

                <div className="mt-14 flex flex-col items-center">
                  <button
                    onClick={handleGeneratePost}
                    disabled={!allNodesFilled || isGenerating}
                    className={`flex items-center gap-3 px-10 py-5 rounded-2xl font-black text-lg transition-all ${
                      allNodesFilled
                        ? 'bg-slate-900 text-white hover:bg-slate-800'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    style={{
                      boxShadow: allNodesFilled ? '6px 6px 0px rgba(0,0,0,0.15)' : 'none',
                      fontFamily: "'Montserrat', sans-serif"
                    }}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-6 h-6" />
                        Generate Post
                      </>
                    )}
                  </button>

                  {!allNodesFilled && (
                    <p className="text-sm font-bold mt-4" style={{ color: '#63a5d3' }}>
                      Fill all sections to generate
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          )} {/* end canvas/preview ternary */}
        </main>

        {/* RIGHT PANEL - Setup/Identity */}
        <aside className={`${mobileTab === 'settings' ? 'flex flex-col' : 'hidden'} lg:flex lg:flex-col h-full w-full lg:w-80 flex-shrink-0 border-b-3 lg:border-b-0 lg:border-l-3 border-slate-200 bg-white/60 backdrop-blur-sm overflow-y-auto pt-24 lg:pt-20 px-5 pb-8 lg:pb-8`} style={{ borderLeftWidth: '3px' }}>
          <div className="space-y-8">

            {/* Pegman Section */}
            <div className="hidden lg:block">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Edit Tool
              </h3>
              <div className="flex items-center gap-4 p-4 rounded-2xl border-2"
                style={{
                  background: 'linear-gradient(to bottom right, #f0f7fb, #e3f0f7)',
                  borderColor: '#c8e1f0',
                }}
              >
                <Pegman />
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium leading-tight" style={{ color: '#2d5a7a' }}>
                    Drag onto canvas or nodes for AI help
                  </p>
                </div>
              </div>
            </div>

            {/* Inspiration - Paste LinkedIn Post */}
            <div>
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Inspiration
              </h3>
              <InspirationPasteBox
                value={inspirationText}
                onChange={setInspirationText}
                onAnalyze={handleAnalyzePost}
                isAnalyzing={isAnalyzingPost}
              />
            </div>

            {/* Identity Section - Collapsable */}
            <div>
              <button
                onClick={() => setIdentityCollapsed(v => !v)}
                className="w-full flex items-center justify-between mb-4 group"
              >
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Your Identity
                </h3>
                <ArrowDown
                  className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-transform duration-200"
                  style={{ transform: identityCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                />
              </button>
              {!identityCollapsed && (
                <div
                  className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden"
                  style={{ boxShadow: '4px 4px 0px rgba(0,0,0,0.05)' }}
                >
                  <textarea
                    value={identityText}
                    onChange={(e) => setIdentityText(e.target.value)}
                    placeholder="Loading your profile..."
                    className="w-full h-64 p-4 text-sm font-medium text-slate-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-inset placeholder:text-slate-400"
                    onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#a8d0e8')}
                    style={{ '--tw-ring-color': '#a8d0e8', fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
              )}
            </div>

            {/* Control Panel */}
            <div>
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Post Settings
              </h3>
              <div
                className="bg-white rounded-2xl border-2 border-slate-200 p-4"
                style={{ boxShadow: '4px 4px 0px rgba(0,0,0,0.05)' }}
              >
                <ControlPanel
                  postLength={postLength} setPostLength={setPostLength}
                  postTone={postTone} setPostTone={setPostTone}
                  includeEmojis={includeEmojis} setIncludeEmojis={setIncludeEmojis}
                  includeHashtags={includeHashtags} setIncludeHashtags={setIncludeHashtags}
                  includeCTA={includeCTA} setIncludeCTA={setIncludeCTA}
                  includeHook={includeHook} setIncludeHook={setIncludeHook}
                />
              </div>
            </div>

          </div>
        </aside>

      </div>

      {/* Stripe Checkout Sidebar for Writing Tool */}
      {showCheckoutSidebar && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40"
            style={{ backdropFilter: 'blur(6px)' }}
            onClick={() => setShowCheckoutSidebar(false)}
          />

          {/* Sidebar */}
          <div
            className="fixed top-0 right-0 h-full w-full sm:w-[480px] z-50 shadow-2xl transition-transform duration-300 ease-in-out"
            style={{
              background: 'rgba(255, 250, 245, 0.98)',
              backdropFilter: 'blur(12px)',
              borderLeft: '1px solid rgba(15, 23, 42, 0.1)',
              fontFamily: "'Inter', sans-serif",
              transform: showCheckoutSidebar ? 'translateX(0)' : 'translateX(100%)',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div
                className="flex items-center justify-between px-6 py-4 border-b"
                style={{ borderColor: 'rgba(15, 23, 42, 0.12)' }}
              >
                <div>
                  <h2 className="text-xl font-bold" style={{ color: '#020617' }}>
                    Unlock the Creative Writing Studio
                  </h2>
                  <p className="text-xs text-slate-600 mt-1">
                    Start a <span className="font-semibold">2‑day free trial</span>. You can cancel anytime before the first
                    charge.
                  </p>
                </div>
                <button
                  onClick={() => setShowCheckoutSidebar(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
                >
                  <X className="w-4 h-4 text-slate-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {!pricingTableBlocked ? (
                  <div className="w-full">
                    <stripe-pricing-table
                      ref={pricingTableRef}
                      pricing-table-id={process.env.REACT_APP_STRIPE_PRICING_TABLE_ID}
                      publishable-key={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
                      customer-email={user?.email}
                    />
                  </div>
                ) : (
                  <div className="w-full">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-900">
                        Stripe checkout is being blocked by a browser extension.
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        No worries — you can still start your 2‑day free trial using secure hosted Stripe Checkout.
                      </p>
                      <div className="mt-4 grid grid-cols-1 gap-2">
                        <button
                          onClick={() => startRedirectCheckout('base')}
                          disabled={redirectCheckoutLoading != null}
                          className="w-full px-4 py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 disabled:opacity-60"
                        >
                          {redirectCheckoutLoading === 'base' ? 'Opening Base checkout…' : 'Start Base (2‑day free trial)'}
                        </button>
                        <button
                          onClick={() => startRedirectCheckout('pro')}
                          disabled={redirectCheckoutLoading != null}
                          className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                        >
                          {redirectCheckoutLoading === 'pro' ? 'Opening Pro checkout…' : 'Start Pro (2‑day free trial)'}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-3">
                        Tip: If you disable your ad blocker for this site, the embedded pricing table will appear.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI Help Modal */}
      <AnimatePresence>
        {showAiHelpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAiHelpModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl border-3 border-slate-200 p-6 w-full max-w-md"
              style={{ boxShadow: '8px 8px 0px rgba(0,0,0,0.1)', borderWidth: '3px' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-slate-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  What would you like to change?
                </h3>
                <button
                  onClick={() => setShowAiHelpModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <textarea
                value={aiHelpRequest}
                onChange={(e) => setAiHelpRequest(e.target.value)}
                placeholder="E.g., 'Make the first section more engaging' or 'Add a section about my achievements' or 'Remove the third section'..."
                className="w-full h-32 p-4 text-sm font-medium text-slate-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-inset placeholder:text-slate-400 border-2 border-slate-200 rounded-xl mb-4"
                style={{ 
                  fontFamily: "'Inter', sans-serif",
                  '--tw-ring-color': '#a8d0e8'
                }}
                onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#a8d0e8')}
                autoFocus
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAiHelpModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAiHelpRequest}
                  disabled={!aiHelpRequest.trim()}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#63a5d3' }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#4a8bb8')}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#63a5d3')}
                >
                  Apply Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Poll Modal - Non-dismissible */}
      <AnimatePresence>
        {showPollModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl border-3 border-slate-200 p-8 w-full max-w-md"
              style={{ boxShadow: '12px 12px 0px rgba(0,0,0,0.15)', borderWidth: '3px' }}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-black text-slate-800 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  How much would you pay per month?
                </h3>
                <p className="text-sm text-slate-600">
                  Help us understand what this tool is worth to you
                </p>
              </div>
              
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handlePollAnswer('$8')}
                  className="w-full px-6 py-4 rounded-2xl text-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                  style={{ 
                    backgroundColor: '#63a5d3',
                    boxShadow: '4px 4px 0px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4a8bb8'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#63a5d3'}
                >
                  $8/month
                </button>
                <button
                  onClick={() => handlePollAnswer('$15')}
                  className="w-full px-6 py-4 rounded-2xl text-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                  style={{ 
                    backgroundColor: '#63a5d3',
                    boxShadow: '4px 4px 0px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4a8bb8'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#63a5d3'}
                >
                  $15/month
                </button>
                <button
                  onClick={() => handlePollAnswer('$20')}
                  className="w-full px-6 py-4 rounded-2xl text-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                  style={{ 
                    backgroundColor: '#63a5d3',
                    boxShadow: '4px 4px 0px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4a8bb8'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#63a5d3'}
                >
                  $20/month
                </button>
              </div>

              <p className="text-xs text-center text-slate-500">
                Your response will help us price this feature
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WritingPage;
